import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import {
    Alert, Modal, PrimaryButton, SecondaryButton, TextInput,
    Select, SelectOption, SelectOptionValue, CheckboxInput,
} from '@luminalearning/components';
import { Language, LuminaQualification } from '@luminalearning/types';
import LANGUAGES from 'src/graphql/queries/languages';
import { qualificationTabClass } from 'src/Components/Developer/qualificationTabUtils';

type Props = {
    isOpen: boolean;
    setOpen: (isOpen: boolean) => void;
    productQualifications: LuminaQualification[] | null | undefined;
    onSubmit: (values: {
        projectName: string;
        primaryLanguageCode: string;
        secondaryLanguageCode: string;
        productIds: number[];
    }) => Promise<string | undefined>;
    isSubmitting?: boolean;
};

const CreateTestProjectModal = ({
    isOpen, setOpen, productQualifications, onSubmit, isSubmitting,
}: Props) => {
    const intl = useIntl();
    const { data: languagesData, loading: languagesLoading } = useQuery(LANGUAGES);

    const [projectName, setProjectName] = useState('');
    const [primaryLanguage, setPrimaryLanguage] = useState<SelectOptionValue | null>(null);
    const [secondaryLanguage, setSecondaryLanguage] = useState<SelectOptionValue | null>(null);
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
    const [activeQualificationTab, setActiveQualificationTab] = useState(0);
    const [submitStatus, setSubmitStatus] = useState<{ tone: 'Success' | 'Error'; message: string } | null>(null);

    const languageOptions: SelectOptionValue[] = useMemo(
        () => {
            const langs: Language[] = languagesData?.languages ?? [];
            return langs
                .filter((lang): lang is Language & { code: string } => !!lang.code)
                .map((lang) => ({
                    id: lang.code,
                    value: `${lang.nativeName} (${lang.name})`,
                }));
        },
        [languagesData]
    );

    const englishUkDefault = useMemo(
        () => languageOptions.find((opt) => opt.id === 'en-GB') ?? null,
        [languageOptions]
    );

    // Set defaults once languages are loaded
    useEffect(() => {
        if (englishUkDefault && !primaryLanguage) {
            setPrimaryLanguage(englishUkDefault);
        }
        if (englishUkDefault && !secondaryLanguage) {
            setSecondaryLanguage(englishUkDefault);
        }
    }, [englishUkDefault, primaryLanguage, secondaryLanguage]);

    const toggleProduct = (productId: number) => {
        setSelectedProductIds((prev) => (prev.includes(productId)
            ? prev.filter((id) => id !== productId)
            : [...prev, productId]));
    };

    const handleSubmit = async () => {
        if (!projectName.trim() || !primaryLanguage || !secondaryLanguage) {
            return;
        }
        setSubmitStatus(null);
        const response = await onSubmit({
            projectName: projectName.trim(),
            primaryLanguageCode: String(primaryLanguage.id),
            secondaryLanguageCode: String(secondaryLanguage.id),
            productIds: selectedProductIds,
        });
        if (response === 'SUCCESS') {
            setSubmitStatus({
                tone: 'Success',
                message: intl.formatMessage({
                    id: 'LP_DEVELOPER_CREATE_PROJECT_SUCCESS',
                    defaultMessage: 'Project created successfully.',
                }),
            });
        } else if (response === 'BAD_REQUEST') {
            setSubmitStatus({
                tone: 'Error',
                message: intl.formatMessage({
                    id: 'LP_DEVELOPER_CREATE_PROJECT_BAD_REQUEST',
                    defaultMessage: 'Invalid request. Please check your inputs and try again.',
                }),
            });
        } else {
            setSubmitStatus({
                tone: 'Error',
                message: intl.formatMessage({
                    id: 'LP_DEVELOPER_CREATE_PROJECT_SERVER_ERROR',
                    defaultMessage: 'Something went wrong. Please try again later.',
                }),
            });
        }
    };

    const handleClose = () => {
        setProjectName('');
        setPrimaryLanguage(englishUkDefault);
        setSecondaryLanguage(englishUkDefault);
        setSelectedProductIds([]);
        setActiveQualificationTab(0);
        setSubmitStatus(null);
        setOpen(false);
    };

    const activeQualification = productQualifications?.[activeQualificationTab] ?? null;

    const isValid = projectName.trim().length > 0 && primaryLanguage !== null && secondaryLanguage !== null;

    return (
        <Modal
            className='w-70 max-h-80%'
            isOpen={isOpen}
            setOpen={handleClose}
            children={
                <div className='flex flex-col space-y-6'>
                    <div className='text-headline-reg font-bold'>
                        <FormattedMessage
                            id="LP_DEVELOPER_CREATE_TEST_PROJECT_TITLE"
                            defaultMessage="Create New Test Project"
                        />
                    </div>

                    <div className='flex flex-col gap-y-2'>
                        <span className='text-label-reg font-semibold'>
                            <FormattedMessage
                                id="LP_DEVELOPER_PROJECT_NAME_LABEL"
                                defaultMessage="Project Name"
                            />
                        </span>
                        <TextInput
                            value={projectName}
                            placeholder={intl.formatMessage({
                                id: 'LP_DEVELOPER_PROJECT_NAME_PLACEHOLDER',
                                defaultMessage: 'Enter project name',
                            })}
                            setValue={setProjectName}
                            className='w-full'
                            isDisabled={isSubmitting}
                        />
                    </div>

                    <div className='flex flex-col gap-y-2'>
                        <span className='text-label-reg font-semibold'>
                            <FormattedMessage
                                id="LP_DEVELOPER_PRIMARY_LANGUAGE_LABEL"
                                defaultMessage="Primary Language"
                            />
                        </span>
                        <Select
                            labelText={intl.formatMessage({
                                id: 'LP_DEVELOPER_PRIMARY_LANGUAGE_LABEL',
                                defaultMessage: 'Primary Language',
                            })}
                            placeholder={intl.formatMessage({
                                id: 'LP_DEVELOPER_SELECT_LANGUAGE_PLACEHOLDER',
                                defaultMessage: 'Select a language',
                            })}
                            value={primaryLanguage}
                            setValue={setPrimaryLanguage}
                            isLoading={languagesLoading}
                            isDisabled={isSubmitting}
                        >
                            {languageOptions.map((option) => (
                                <SelectOption key={option.id} value={option} />
                            ))}
                        </Select>
                    </div>

                    <div className='flex flex-col gap-y-2'>
                        <span className='text-label-reg font-semibold'>
                            <FormattedMessage
                                id="LP_DEVELOPER_SECONDARY_LANGUAGE_LABEL"
                                defaultMessage="Secondary Language"
                            />
                        </span>
                        <Select
                            labelText={intl.formatMessage({
                                id: 'LP_DEVELOPER_SECONDARY_LANGUAGE_LABEL',
                                defaultMessage: 'Secondary Language',
                            })}
                            placeholder={intl.formatMessage({
                                id: 'LP_DEVELOPER_SELECT_LANGUAGE_PLACEHOLDER',
                                defaultMessage: 'Select a language',
                            })}
                            value={secondaryLanguage}
                            setValue={setSecondaryLanguage}
                            isLoading={languagesLoading}
                            isDisabled={isSubmitting}
                        >
                            {languageOptions.map((option) => (
                                <SelectOption key={option.id} value={option} />
                            ))}
                        </Select>
                    </div>

                    {productQualifications && productQualifications.length > 0 && (
                        <div className='flex flex-col gap-y-4'>
                            <span className='text-label-reg font-semibold'>
                                <FormattedMessage
                                    id="LP_DEVELOPER_ENABLED_PRODUCTS_LABEL"
                                    defaultMessage="Enabled Products"
                                />
                            </span>
                            <div className='flex flex-row flex-wrap gap-2' role='tablist'>
                                {productQualifications.map((qual, index) => (
                                    activeQualificationTab === index ? (
                                        <PrimaryButton
                                            key={qual.qualificationName}
                                            type='button'
                                            text={qual.qualificationName}
                                            onClick={() => setActiveQualificationTab(index)}
                                            className={qualificationTabClass}
                                        />
                                    ) : (
                                        <SecondaryButton
                                            key={qual.qualificationName}
                                            type='button'
                                            text={qual.qualificationName}
                                            onClick={() => setActiveQualificationTab(index)}
                                            className={qualificationTabClass}
                                        />
                                    )
                                ))}
                            </div>
                            {activeQualification && (
                                <div className='grid grid-cols-4 gap-y-2 gap-x-4'>
                                    {(activeQualification.products ?? []).filter((p) => p.name).map((product) => (
                                        <div key={product.id} className='flex flex-row items-center gap-x-2'>
                                            <CheckboxInput
                                                inputId={`product-${product.id}`}
                                                isChecked={selectedProductIds.includes(product.id)}
                                                setChecked={() => toggleProduct(product.id)}
                                                isDisabled={isSubmitting}
                                                className='w-8'
                                            />
                                            <label
                                                htmlFor={`product-${product.id}`}
                                                style={{ color: product.colourTheme ?? undefined }}
                                            >
                                                {product.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {submitStatus && (
                        <Alert type={submitStatus.tone} message={submitStatus.message} />
                    )}

                    <div className='flex flex-row justify-end space-x-4'>
                        <SecondaryButton
                            type='button'
                            text={intl.formatMessage({
                                id: 'LP_CANCEL',
                                defaultMessage: 'Cancel',
                            })}
                            onClick={handleClose}
                            isDisabled={isSubmitting}
                        />
                        <PrimaryButton
                            type='button'
                            text={intl.formatMessage({
                                id: 'LP_CREATE',
                                defaultMessage: 'Create',
                            })}
                            onClick={handleSubmit}
                            isDisabled={!isValid || isSubmitting}
                            isLoading={isSubmitting}
                        />
                    </div>
                </div>
            }
        />
    );
};

export default CreateTestProjectModal;
